<template>
  <div class="dashboard-container">
    <!-- HEADER SECTION -->
    <b-card class="mb-3 dashboard-header" no-body>
      <b-card-body class="d-flex justify-content-between align-items-center py-2">
        <div>
          <div class="d-flex align-items-center">
            <div class="header-icon-wrapper mr-2">
              <b-icon-file-earmark-text class="header-icon" />
            </div>
            <div>
              <h5 class="mb-0">DAILY BATCH REPORT</h5>
              <small class="header-subtitle">Daily closeout summaries and batch totals</small>
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center">
          <b-badge variant="light" class="mr-2 date-badge">
            <b-icon-calendar class="mr-1" />{{ formattedDateRange }}
          </b-badge>
          <b-badge variant="primary" pill class="animated-badge">
            {{ filter.length }} {{ filter.length === 1 ? 'Batch' : 'Batches' }}
          </b-badge>
        </div>
      </b-card-body>
    </b-card>

    <!-- FILTERS CARD -->
    <b-card class="mb-3 filters-card" no-body>
      <b-card-body class="py-2">
        <div class="row align-items-end">
          <div class="col-lg-8">
            <div class="d-flex flex-wrap align-items-end" style="gap: 10px;">
              <div style="min-width: 250px;">
                <label class="small text-muted mb-1">
                  <b-icon-calendar class="mr-1" size="sm" />DATE RANGE
                </label>
                <date-picker
                  v-model="date_range"
                  format="MM-DD-YYYY"
                  valueType="format"
                  class="custom-datepicker"
                  placeholder="SELECT DATE RANGE"
                  range
                  :class="{ 'datepicker-focused': dateRangeFocused }"
                  @focus="dateRangeFocused = true"
                  @blur="dateRangeFocused = false"
                ></date-picker>
              </div>

              <div class="d-flex flex-wrap" style="gap: 5px;">
                <b-button
                  v-for="btn in quickDateButtons"
                  :key="btn.id"
                  size="sm"
                  :variant="picked_date_range === btn.id ? 'danger' : 'outline-danger'"
                  @click="set_dates(btn.id)"
                  class="quick-date-btn py-1 px-2"
                >
                  <b-icon v-if="picked_date_range === btn.id" :icon="btn.icon" class="mr-1" size="sm" />
                  {{ btn.label }}
                </b-button>
              </div>
            </div>
          </div>
          
          <div class="col-lg-4 mt-2 mt-lg-0">
            <div class="d-flex justify-content-end">
              <b-button
                variant="success"
                @click="fetch_batch_data"
                class="search-btn"
                :disabled="loading"
              >
                <b-icon-search class="mr-1" /> 
                <span v-if="!loading">SEARCH</span>
                <span v-else>
                  <b-icon-arrow-repeat spin class="mr-1" />LOADING...
                </span>
              </b-button>
            </div>
          </div>
        </div>

        <!-- ADVANCED FILTERS -->
        <div class="mt-2">
          <b-button 
            variant="link" 
            size="sm" 
            @click="showAdvancedFilters = !showAdvancedFilters"
            class="p-0 text-decoration-none"
          >
            <b-icon :icon="showAdvancedFilters ? 'chevron-up' : 'chevron-down'" class="mr-1" size="sm" />
            {{ showAdvancedFilters ? 'Hide' : 'Show' }} Filters
          </b-button>
          
          <b-collapse v-model="showAdvancedFilters">
            <div class="mt-2 pt-2 border-top">
              <div class="row">
                <!-- LOCATION FILTER -->
                <div class="col-md-3 mb-2">
                  <label class="small text-muted mb-1">
                    <b-icon-building class="mr-1" size="sm" />LOCATION
                  </label>
                  <b-form-select
                    v-model="picked_location"
                    :options="filteredLocationOptions"
                    size="sm"
                    class="filter-select"
                    @change="onFilterChange"
                  ></b-form-select>
                </div>

                <!-- PAYMENT METHOD FILTER -->
                <div class="col-md-3 mb-2">
                  <label class="small text-muted mb-1">
                    <b-icon-credit-card class="mr-1" size="sm" />PAYMENT METHOD
                  </label>
                  <b-form-select
                    v-model="picked_payment_method"
                    :options="paymentMethodOptions"
                    size="sm"
                    class="filter-select"
                    @change="onFilterChange"
                  ></b-form-select>
                </div>

                <!-- FINANCE COMPANY FILTER -->
                <div class="col-md-3 mb-2">
                  <label class="small text-muted mb-1">
                    <b-icon-bank class="mr-1" size="sm" />FINANCE COMPANY
                  </label>
                  <b-form-select
                    v-model="picked_finance_company"
                    :options="financeCompanyOptions"
                    size="sm"
                    class="filter-select"
                    @change="onFilterChange"
                  ></b-form-select>
                </div>

                <!-- BATCH STATUS FILTER -->
                <div class="col-md-3 mb-2">
                  <label class="small text-muted mb-1">
                    <b-icon-check-circle class="mr-1" size="sm" />BATCH STATUS
                  </label>
                  <b-form-select
                    v-model="picked_batch_status"
                    :options="batchStatusOptions"
                    size="sm"
                    class="filter-select"
                    @change="onFilterChange"
                  ></b-form-select>
                </div>
              </div>
            </div>
          </b-collapse>
        </div>
      </b-card-body>
    </b-card>

    <!-- LOADING STATE -->
    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary" type="grow" label="Loading..."></b-spinner>
      <p class="mt-2 text-muted small">Fetching batch data...</p>
    </div>

    <!-- MAIN CONTENT -->
    <template v-else-if="filter.length > 0">
      <!-- COMPACT KPI CARDS -->
      <div class="row mb-2">
        <div class="col-xl-3 col-lg-6 col-md-6 mb-2">
          <div class="compact-kpi-card">
            <div class="compact-kpi-icon bg-primary">
              <b-icon-cash-stack />
            </div>
            <div class="compact-kpi-info">
              <span class="compact-kpi-label">Gross Sales</span>
              <span class="compact-kpi-value">${{ formatNumber(totalGrossSales) }}</span>
              <small class="compact-kpi-sub">{{ filter.length }} batches</small>
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-lg-6 col-md-6 mb-2">
          <div class="compact-kpi-card">
            <div class="compact-kpi-icon bg-success">
              <b-icon-wallet2 />
            </div>
            <div class="compact-kpi-info">
              <span class="compact-kpi-label">Net Sales</span>
              <span class="compact-kpi-value">${{ formatNumber(totalNetSales) }}</span>
              <small class="compact-kpi-sub">Gross - Refund - Paid Out - Short</small>
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-lg-6 col-md-6 mb-2">
          <div class="compact-kpi-card">
            <div class="compact-kpi-icon bg-warning">
              <b-icon-calculator />
            </div>
            <div class="compact-kpi-info">
              <span class="compact-kpi-label">Total Cash</span>
              <span class="compact-kpi-value">${{ formatNumber(totalCash) }}</span>
              <small class="compact-kpi-sub">Net cash</small>
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-lg-6 col-md-6 mb-2">
          <div class="compact-kpi-card">
            <div class="compact-kpi-icon bg-info">
              <b-icon-credit-card />
            </div>
            <div class="compact-kpi-info">
              <span class="compact-kpi-label">Credit/Finance</span>
              <span class="compact-kpi-value">${{ formatNumber(totalCreditFinance) }}</span>
              <small class="compact-kpi-sub">Credit + Finance</small>
            </div>
          </div>
        </div>
      </div>

      <!-- DATA TABLE SECTION -->
      <b-card no-body class="mt-2 data-table-card">
        <b-card-header class="bg-light d-flex justify-content-between align-items-center py-1">
          <div class="d-flex align-items-center">
            <b-icon-table class="mr-2" size="sm" />
            <h6 class="mb-0">Daily Batch Details</h6>
            <b-badge variant="secondary" class="ml-2">{{ filter.length }} {{ filter.length === 1 ? 'record' : 'records' }}</b-badge>
          </div>
          <div>
            <b-button size="sm" variant="outline-secondary" @click="exportToCSV" :disabled="filter.length === 0" class="mr-1">
              <b-icon-download class="mr-1" size="sm" /> Export
            </b-button>
            <b-button size="sm" variant="outline-primary" @click="fetch_batch_data">
              <b-icon-arrow-repeat class="mr-1" size="sm" /> Refresh
            </b-button>
          </div>
        </b-card-header>
        <b-card-body class="p-0">
          <b-overlay :show="loading" spinner-variant="primary" spinner-type="grow" spinner-small rounded="sm">
            <div v-if="filter.length > 0" class="table-wrapper-sm">
              <table class="compact-table">
                <thead>
                  <tr>
                    <th @click="sortby('id')">BATCH #</th>
                    <th @click="sortby('close_date')">DATE</th>
                    <th @click="sortby('day')">DAY</th>
                    <th @click="sortby('location_name')">LOCATION</th>
                    <th @click="sortby('username')">AGENT</th>
                    <th class="text-right" @click="sortby('net_cash')">CASH</th>
                    <th class="text-right" @click="sortby('credit_card_total')">CREDIT</th>
                    <th class="text-right" @click="sortby('other_finance_total')">FINANCE</th>
                    <th class="text-center" style="min-width: 80px;">
                      FINANCE DETAILS
                      <b-button 
                        size="sm" 
                        variant="link" 
                        class="p-0 ml-1 finance-toggle-header"
                        @click="toggleAllFinancePopovers"
                        :title="showAllFinancePopovers ? 'Hide all finance details' : 'Show all finance details'"
                      >
                        <b-icon :icon="showAllFinancePopovers ? 'eye-slash' : 'eye'" size="sm" />
                      </b-button>
                    </th>
                    <th class="text-right" @click="sortby('total_refunds')">REFUND</th>
                    <th class="text-right" @click="sortby('paid_out_amount')">PAID OUT</th>
                    <th class="text-right" @click="sortby('total_short_over')">SHORT/OVER</th>
                    <th class="text-right" @click="sortby('gross_sales')">GROSS</th>
                    <th class="text-right" @click="sortby('net_sales')">NET</th>
                    <th class="text-center">PDF</th>
                    <th class="text-center">IMAGES</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- TOTALS ROW -->
                  <tr class="totals-row" v-if="filter.length > 0">
                    <td colspan="5" class="text-right"><strong>TOTALS:</strong></td>
                    <td class="text-right"><strong>${{ formatNumber(total('net_cash')) }}</strong></td>
                    <td class="text-right"><strong>${{ formatNumber(total('credit_card_total')) }}</strong></td>
                    <td class="text-right"><strong>${{ formatNumber(total('other_finance_total')) }}</strong></td>
                    <td class="text-center"><strong>—</strong></td>
                    <td class="text-right"><strong>${{ formatNumber(total('total_refunds')) }}</strong></td>
                    <td class="text-right"><strong>${{ formatNumber(total('paid_out_amount')) }}</strong></td>
                    <td class="text-right"><strong>${{ formatNumber(total('total_short_over')) }}</strong></td>
                    <td class="text-right"><strong>${{ formatNumber(total('gross_sales')) }}</strong></td>
                    <td class="text-right"><strong>${{ formatNumber(total('net_sales')) }}</strong></td>
                    <td colspan="2"></td>
                  </tr>
                  
                  <!-- DATA ROWS -->
                  <tr v-for="(item, idx) in paginatedItems" :key="idx" 
                      v-if="item"
                      :class="{ 'highlight-short-row': item.total_short_over && parseFloat(item.total_short_over) !== 0 }"
                      :style="{ background: item ? getRowBackgroundColor(item) : 'transparent' }">
                    <td><strong>{{ item.id || '-' }}</strong></td>
                    <td>{{ formatDate(item.close_date) }}</td>
                    <td>{{ getDayOfWeek(item.close_date) }}</td>
                    <td>{{ getLocationName(item.location_id) }}</td>
                    <td>{{ item.username || '-' }}</td>
                    <td class="text-right" :class="getAmountClass(item.net_cash)">${{ formatNumber(item.net_cash) }}</td>
                    <td class="text-right" :class="getAmountClass(item.credit_card_total)">${{ formatNumber(item.credit_card_total) }}</td>
                    <td class="text-right" :class="getAmountClass(item.other_finance_total)">${{ formatNumber(item.other_finance_total) }}</td>
                    <td class="text-center">
                      <template v-if="hasFinanceDetails(item)">
                        <b-button 
                          size="sm" 
                          variant="outline-info" 
                          v-b-popover.hover.html="getFinancePopoverContent(item)"
                          :title="`Finance Breakdown - Batch #${item.id}`"
                          :triggers="showAllFinancePopovers ? 'click blur' : 'click blur'"
                          placement="left"
                          class="finance-detail-btn"
                          @click="toggleFinancePopover(item, $event)"
                          ref="popover"
                        >
                          <b-icon-grid3x3-gap-fill size="sm" class="mr-1" />
                          <span>{{ Object.keys(item.finance_companies).length }}</span>
                          <b-icon-chevron-down size="sm" class="ml-1" />
                        </b-button>
                      </template>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td class="text-right text-danger" :class="getAmountClass(item.total_refunds, true)">${{ formatNumber(item.total_refunds) }}</td>
                    <td class="text-right text-danger" :class="getAmountClass(item.paid_out_amount)">${{ formatNumber(item.paid_out_amount) }}</td>
                    <td class="text-right" :class="getShortOverClass(item.total_short_over)">${{ formatNumber(item.total_short_over) }}</td>
                    <td class="text-right text-warning" :class="getAmountClass(item.gross_sales)">${{ formatNumber(item.gross_sales) }}</td>
                    <td class="text-right text-success" :class="getAmountClass(item.net_sales)">${{ formatNumber(item.net_sales) }}</td>
                    <td class="text-center">
                      <b-button 
                        v-if="item.pdf_url" 
                        size="sm" 
                        variant="outline-primary" 
                        @click="openPDF(item.pdf_url)"
                        title="View PDF"
                        class="action-btn"
                      >
                        <b-icon-file-earmark-pdf size="sm" />
                      </b-button>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td class="text-center">
                      <div v-if="item.paid_out_images && item.paid_out_images.length > 0">
                        <b-button 
                          size="sm" 
                          variant="outline-info" 
                          @click="openImageGallery(item)"
                          title="View Images"
                          class="action-btn"
                        >
                          <b-icon-images size="sm" />
                          <span class="ml-1">{{ getImageCount(item) }}</span>
                        </b-button>
                      </div>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <!-- Pagination -->
            <div v-if="filter.length > perPage" class="pagination-wrapper d-flex justify-content-between align-items-center p-2 bg-light">
              <small class="text-muted">
                Showing {{ ((currentPage - 1) * perPage) + 1 }} to {{ Math.min(currentPage * perPage, filter.length) }} of {{ filter.length }} records
              </small>
              <b-pagination
                v-model="currentPage"
                :total-rows="filter.length"
                :per-page="perPage"
                size="sm"
                class="mb-0"
                align="center"
              ></b-pagination>
              <b-form-select v-model="perPage" size="sm" style="width: 65px;" @change="currentPage = 1">
                <option :value="10">10</option>
                <option :value="25">25</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
              </b-form-select>
            </div>
          </b-overlay>
        </b-card-body>
      </b-card>

      <!-- SUMMARY SECTION -->
      <b-card class="mt-3 summary-card" no-body>
        <b-card-body class="py-2">
          <div class="row text-center">
            <div class="col-md-3 col-6 mb-2">
              <span class="summary-label">GROSS SALES</span>
              <h5 class="summary-value text-primary">${{ formatNumber(totalGrossSales) }}</h5>
            </div>
            <div class="col-md-3 col-6 mb-2">
              <span class="summary-label">NET SALES</span>
              <h5 class="summary-value text-success">${{ formatNumber(totalNetSales) }}</h5>
            </div>
            <div class="col-md-3 col-6 mb-2">
              <span class="summary-label">SHORT/OVER</span>
              <h5 :class="['summary-value', totalShortOverClass]">${{ formatNumber(totalShortOver) }}</h5>
            </div>
            <div class="col-md-3 col-6 mb-2">
              <span class="summary-label">TOTAL BATCHES</span>
              <h5 class="summary-value">{{ filter.length }}</h5>
            </div>
          </div>
        </b-card-body>
      </b-card>
    </template>

    <!-- EMPTY STATE -->
    <div v-else-if="!loading && batchData.length === 0" class="text-center py-4">
      <b-alert show variant="light" class="mb-0">
        <b-icon-inbox class="mr-2" />
        No batch data available. Select a date range and click SEARCH.
      </b-alert>
    </div>

    <!-- IMAGE GALLERY MODAL -->
    <b-modal 
      v-model="showImageModal" 
      :title="`Paid Out Images - Batch #${selectedBatch ? selectedBatch.id : ''}`"
      size="lg"
      hide-footer
      @hidden="resetImageGallery"
    >
      <div v-if="selectedImages.length > 0" class="image-gallery">
        <div class="row">
          <div class="col-md-6 col-lg-4 mb-3" v-for="(image, idx) in selectedImages" :key="idx">
            <div class="image-card">
              <img 
                :src="image" 
                :alt="`Paid out image ${idx + 1}`"
                class="img-fluid rounded"
                @click="viewFullImage(image)"
                style="cursor: pointer; width: 100%; height: 150px; object-fit: cover;"
              />
              <div class="image-actions mt-1 d-flex justify-content-between">
                <b-button size="sm" variant="outline-primary" @click="openImageInNewTab(image)">
                  <b-icon-box-arrow-up-right size="sm" /> View
                </b-button>
                <b-button size="sm" variant="outline-success" @click="downloadImage(image, idx)">
                  <b-icon-download size="sm" /> Download
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center py-4">
        <b-icon-image class="text-muted" style="font-size: 48px;" />
        <p class="mt-2 text-muted">No images available</p>
      </div>
    </b-modal>

    <!-- FULL IMAGE MODAL -->
    <b-modal 
      v-model="fullImageModal" 
      size="xl"
      hide-footer
      title="Full Image"
    >
      <div class="text-center">
        <img :src="fullImageUrl" class="img-fluid" style="max-height: 80vh;" />
      </div>
      <div class="mt-3 text-center">
        <b-button variant="outline-primary" @click="openImageInNewTab(fullImageUrl)">
          <b-icon-box-arrow-up-right /> Open in New Tab
        </b-button>
        <b-button variant="outline-success" @click="downloadImage(fullImageUrl)" class="ml-2">
          <b-icon-download /> Download
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';
import 'moment/locale/en-gb';
import Requestor from '@/utilities/Requestor';

import {
  BIconFileEarmarkText,
  BIconCalendar,
  BIconSearch,
  BIconCashStack,
  BIconWallet2,
  BIconCalculator,
  BIconCreditCard,
  BIconTable,
  BIconDownload,
  BIconFileEarmarkPdf,
  BIconImages,
  BIconBoxArrowUpRight,
  BIconArrowRepeat,
  BIconBuilding,
  BIconInbox,
  BIconChevronUp,
  BIconChevronDown,
  BIconImage,
  BIconEye,
  BIconEyeSlash,
  BIconGrid3x3GapFill,
  BIconBank,
  BIconCheckCircle
} from 'bootstrap-vue';

export default {
  name: "DailyBatchReport",
  components: {
    DatePicker,
    BIconFileEarmarkText,
    BIconCalendar,
    BIconSearch,
    BIconCashStack,
    BIconWallet2,
    BIconCalculator,
    BIconCreditCard,
    BIconTable,
    BIconDownload,
    BIconFileEarmarkPdf,
    BIconImages,
    BIconBoxArrowUpRight,
    BIconArrowRepeat,
    BIconBuilding,
    BIconInbox,
    BIconChevronUp,
    BIconChevronDown,
    BIconImage,
    BIconEye,
    BIconEyeSlash,
    BIconGrid3x3GapFill,
    BIconBank,
    BIconCheckCircle
  },
  data() {
    return {
      packet: {
        userDetails: {},
        locations: []
      },
      batchData: [],
      loading: false,
      date_range: [],
      picked_date_range: null,
      picked_location: null,
      picked_payment_method: null,
      picked_finance_company: null,
      picked_batch_status: null,
      sort_asc: false,
      sort_field: 'id',
      currentPage: 1,
      perPage: 25,
      showAdvancedFilters: false,
      dateRangeFocused: false,
      
      // Finance details toggle
      showAllFinancePopovers: false,
      financePopoverStates: {},
      
      // Image gallery data
      showImageModal: false,
      selectedBatch: null,
      selectedImages: [],
      fullImageModal: false,
      fullImageUrl: '',
      
      // Quick date buttons
      quickDateButtons: [
        { id: 'today', label: 'Today', icon: 'calendar-day' },
        { id: 'yesterday', label: 'Yesterday', icon: 'calendar-day' },
        { id: 'this_week', label: 'This Week', icon: 'calendar-week' },
        { id: 'last_week', label: 'Last Week', icon: 'calendar-week' },
        { id: 'this_month', label: 'This Month', icon: 'calendar-month' },
        { id: 'last_month', label: 'Last Month', icon: 'calendar-month' }
      ],
      
      // Location options
      location_options: [
        { value: null, text: 'ALL LOCATIONS' }
      ],
      
      // Excluded locations
      excludedLocations: ['Mecca Shipping', '9th St 101', 'WH', 'Folcroft WH'],
      
      // Payment method options
      paymentMethodOptions: [
        { value: null, text: 'ALL PAYMENT METHODS' },
        { value: 'cash', text: 'Cash' },
        { value: 'credit', text: 'Credit Card' },
        { value: 'finance', text: 'Finance' },
        { value: 'snap', text: 'SNAP' }
      ],
      
      // Finance company options
      financeCompanyOptions: [
        { value: null, text: 'ALL FINANCE COMPANIES' }
      ],
      
      // Batch status options
      batchStatusOptions: [
        { value: null, text: 'ALL STATUSES' },
        { value: 'complete', text: 'Complete' },
        { value: 'partial', text: 'Partial' },
        { value: 'pending', text: 'Pending' }
      ],
      
      // Location mapping for display
      locationMap: {}
    };
  },
  computed: {
    formattedDateRange() {
      if (this.date_range && this.date_range.length === 2) {
        return `${this.date_range[0]} - ${this.date_range[1]}`;
      }
      return 'Select dates';
    },
    
    filteredLocationOptions() {
      return this.location_options.filter(option => {
        if (option.value === null) return true; // Keep "ALL LOCATIONS"
        const locationName = option.text.toLowerCase();
        return !this.excludedLocations.some(excluded => 
          locationName.includes(excluded.toLowerCase())
        );
      });
    },
    
    filter() {
      let filtered = this.batchData.filter(item => {
        // Location filter
        if (this.picked_location && item.location_id !== this.picked_location) {
          return false;
        }
        
        // Payment method filter
        if (this.picked_payment_method) {
          const hasCash = parseFloat(item.net_cash) > 0;
          const hasCredit = parseFloat(item.credit_card_total) > 0;
          const hasFinance = parseFloat(item.other_finance_total) > 0;
          const hasSnap = parseFloat(item.snap_finance_total) > 0;
          
          switch (this.picked_payment_method) {
            case 'cash':
              if (!hasCash) return false;
              break;
            case 'credit':
              if (!hasCredit) return false;
              break;
            case 'finance':
              if (!hasFinance) return false;
              break;
            case 'snap':
              if (!hasSnap) return false;
              break;
          }
        }
        
        // Finance company filter
        if (this.picked_finance_company && item.finance_companies) {
          const companyNames = Object.keys(item.finance_companies);
          if (!companyNames.includes(this.picked_finance_company)) {
            return false;
          }
        }
        
        // Batch status filter
        if (this.picked_batch_status) {
          // Add your status logic here based on your data structure
          // This is a placeholder - adjust based on your actual data
          const status = item.status || 'complete';
          if (status !== this.picked_batch_status) {
            return false;
          }
        }
        
        return true;
      });
      
      // Apply sorting
      if (this.sort_field) {
        filtered = filtered.sort((a, b) => {
          let aVal = a[this.sort_field] || 0;
          let bVal = b[this.sort_field] || 0;
          
          if (this.sort_field === 'close_date') {
            aVal = new Date(aVal).getTime();
            bVal = new Date(bVal).getTime();
          }
          
          if (typeof aVal === 'string' && typeof bVal === 'string') {
            return this.sort_asc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
          }
          
          return this.sort_asc ? parseFloat(aVal) - parseFloat(bVal) : parseFloat(bVal) - parseFloat(aVal);
        });
      }
      
      return filtered;
    },
    
    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filter.slice(start, end);
    },
    
    totalGrossSales() {
      return this.filter.reduce((sum, item) => sum + (parseFloat(item.gross_sales) || 0), 0);
    },
    
    totalNetSales() {
      return this.filter.reduce((sum, item) => sum + (parseFloat(item.net_sales) || 0), 0);
    },
    
    totalCash() {
      return this.filter.reduce((sum, item) => sum + (parseFloat(item.net_cash) || 0), 0);
    },
    
    totalCreditFinance() {
      return this.filter.reduce((sum, item) => {
        const creditCard = parseFloat(item.credit_card_total) || 0;
        const otherFinance = parseFloat(item.other_finance_total) || 0;
        const snapFinance = parseFloat(item.snap_finance_total) || 0;
        return sum + creditCard + otherFinance + snapFinance;
      }, 0);
    },
    
    totalShortOver() {
      return this.filter.reduce((sum, item) => sum + (parseFloat(item.total_short_over) || 0), 0);
    },
    
    totalShortOverClass() {
      return this.totalShortOver >= 0 ? 'text-success' : 'text-danger';
    },
    
    locations() {
      return this.packet.locations || [];
    },
    
    // Dynamically build finance company options from data
    financeCompanyOptionsComputed() {
      const companies = new Set();
      this.batchData.forEach(item => {
        if (item.finance_companies) {
          Object.keys(item.finance_companies).forEach(name => {
            companies.add(name);
          });
        }
      });
      
      return [
        { value: null, text: 'ALL FINANCE COMPANIES' },
        ...Array.from(companies).map(name => ({
          value: name,
          text: name
        }))
      ];
    }
  },
  watch: {
    filter() {
      this.currentPage = 1;
    },
    
    // Update finance company options when data loads
    batchData() {
      this.financeCompanyOptions = this.financeCompanyOptionsComputed;
    }
  },
  created() {
    this.initializeUser();
    this.initializeComponent();
  },
  mounted() {
    this.set_dates('this_month');
  },
  methods: {
    initializeUser() {
      try {
        this.packet = JSON.parse(localStorage.getItem('packet') || '{}');
        this.packet.userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
        
        if (this.packet.locations && this.packet.locations.length > 0) {
          this.location_options = [
            { value: null, text: 'ALL LOCATIONS' },
            ...this.packet.locations.map(loc => ({
              value: loc.id || loc.value,
              text: loc.name || loc.text || `Location ${loc.id || loc.value}`
            }))
          ];
          
          this.packet.locations.forEach(loc => {
            this.locationMap[loc.id || loc.value] = loc.name || loc.text || `Location ${loc.id || loc.value}`;
          });
        }
      } catch (err) {
        console.error("User data parsing failed:", err);
      }
    },
    
    initializeComponent() {
      const today = moment();
      this.date_range = [
        today.startOf('month').format('MM-DD-YYYY'),
        today.endOf('month').format('MM-DD-YYYY')
      ];
    },
    
    set_dates(value) {
      let start = null;
      let end = null;
      
      switch (value) {
        case 'today':
          start = moment().format('MM-DD-YYYY');
          end = moment().format('MM-DD-YYYY');
          break;
        case 'yesterday':
          start = moment().subtract(1, 'days').format('MM-DD-YYYY');
          end = moment().subtract(1, 'days').format('MM-DD-YYYY');
          break;
        case 'this_week':
          start = moment().startOf('week').format('MM-DD-YYYY');
          end = moment().endOf('week').format('MM-DD-YYYY');
          break;
        case 'last_week':
          start = moment().subtract(1, 'week').startOf('week').format('MM-DD-YYYY');
          end = moment().subtract(1, 'week').endOf('week').format('MM-DD-YYYY');
          break;
        case 'this_month':
          start = moment().startOf('month').format('MM-DD-YYYY');
          end = moment().endOf('month').format('MM-DD-YYYY');
          break;
        case 'last_month':
          start = moment().subtract(1, 'month').startOf('month').format('MM-DD-YYYY');
          end = moment().subtract(1, 'month').endOf('month').format('MM-DD-YYYY');
          break;
      }
      
      if (start && end) {
        this.date_range = [start, end];
        this.picked_date_range = value;
        this.fetch_batch_data();
      }
    },
    
    onFilterChange() {
      // Reset pagination when filters change
      this.currentPage = 1;
      // Optionally auto-refresh data when filters change
      // this.fetch_batch_data();
    },
    
    fetch_batch_data() {
      if (!this.date_range || this.date_range.length < 2) {
        this.$bvToast.toast(
          "Please select a date range",
          { title: "Warning", autoHideDelay: 1500, variant: "warning" }
        );
        return;
      }
      
      const dates = this.date_range;
      const that = this;
      
      const data = {
        start_date: dates[0],
        end_date: dates[1],
        location_id: this.picked_location,
        username: this.packet.userDetails.username
      };
      
      const path = "/api/reports/daily_batch_report/";
      
      const successCallback = function(response) {
        if (response.data.result) {
          that.batchData = response.data.result.map(item => {
            let otherFinanceTotal = 0;
            let snapFinanceTotal = 0;
            let financeCompanies = {};
            
            // Process finance companies from metadata
            if (item.metadata && item.metadata.finance_companies_raw) {
              const financeCompaniesRaw = item.metadata.finance_companies_raw;
              financeCompanies = financeCompaniesRaw;
              
              Object.keys(financeCompaniesRaw).forEach(key => {
                const financeItem = financeCompaniesRaw[key];
                const amount = parseFloat(financeItem.amount) || 0;
                
                if (key.toUpperCase().includes('SNAP') || 
                    (financeItem.original_method && financeItem.original_method.toUpperCase().includes('SNAP'))) {
                  snapFinanceTotal += amount;
                } else {
                  otherFinanceTotal += amount;
                }
              });
            }
            
            // Also check if finance_companies was sent from backend
            if (item.finance_companies) {
              financeCompanies = { ...financeCompanies, ...item.finance_companies };
            }
            
            // Fix: Get credit card amount from multiple sources
            let creditCardAmount = parseFloat(item.credit_card_total || 0);
            if (creditCardAmount === 0 && item.net_credit_card) {
              creditCardAmount = parseFloat(item.net_credit_card) || 0;
            }
            if (creditCardAmount === 0 && item.credit_card_amount) {
              creditCardAmount = parseFloat(item.credit_card_amount) || 0;
            }
            
            const grossSales = parseFloat(item.gross_sales || 0);
            const refunds = parseFloat(item.total_refunds || 0);
            const paidOut = parseFloat(item.paid_out_amount || 0);
            const shortOver = parseFloat(item.total_short_over || 0);
            const netSales = grossSales - refunds - paidOut + shortOver;
            
            let paidOutImages = item.paid_out_images || [];
            if (typeof paidOutImages === 'string') {
              try {
                paidOutImages = JSON.parse(paidOutImages);
              } catch (e) {
                paidOutImages = paidOutImages ? [paidOutImages] : [];
              }
            }
            
            if (!Array.isArray(paidOutImages)) {
              paidOutImages = paidOutImages ? [paidOutImages] : [];
            }
            
            return {
              ...item,
              net_cash: parseFloat(item.net_cash || item.cash_amount || 0).toFixed(2),
              credit_card_total: creditCardAmount.toFixed(2),
              other_finance_total: otherFinanceTotal.toFixed(2),
              snap_finance_total: snapFinanceTotal.toFixed(2),
              total_refunds: parseFloat(item.total_refunds || 0).toFixed(2),
              paid_out_amount: parseFloat(item.paid_out_amount || 0).toFixed(2),
              total_short_over: parseFloat(item.total_short_over || 0).toFixed(2),
              gross_sales: grossSales.toFixed(2),
              net_sales: netSales.toFixed(2),
              day: that.getDayOfWeek(item.close_date),
              location_name: that.getLocationName(item.location_id),
              paid_out_images: paidOutImages,
              finance_companies: financeCompanies
            };
          });
          
          // Update finance company options
          that.financeCompanyOptions = that.financeCompanyOptionsComputed;
          
          that.$bvToast.toast(
            `Loaded ${that.batchData.length} batch records (latest per location per day)`,
            { title: "Success", autoHideDelay: 1500, variant: "success" }
          );
        } else {
          that.batchData = [];
          that.$bvToast.toast(
            response.data.error || "No data returned",
            { title: "Error", autoHideDelay: 1500, variant: "warning" }
          );
        }
        that.loading = false;
      };
      
      const errorCallback = function(error) {
        console.log("API Error:", error);
        that.$bvToast.toast(
          "Error occurred. Please try again.",
          { title: "Error", autoHideDelay: 1500, variant: "danger" }
        );
        that.loading = false;
      };
      
      this.loading = true;
      Requestor.postSyncP(path, data, this, successCallback, errorCallback);
    },
    
    getLocationName(locationId) {
      if (!locationId) return 'N/A';
      return this.locationMap[locationId] || `Location ${locationId}`;
    },
    
    sortby(field) {
      if (this.sort_field === field) {
        this.sort_asc = !this.sort_asc;
      } else {
        this.sort_field = field;
        this.sort_asc = false;
      }
    },
    
    total(key) {
      return this.filter.reduce((sum, item) => {
        let value = 0;
        if (key === 'credit_card_total') {
          value = parseFloat(item.credit_card_total || 0);
        } else if (key === 'other_finance_total') {
          value = parseFloat(item.other_finance_total || 0);
        } else if (key === 'snap_finance_total') {
          value = parseFloat(item.snap_finance_total || 0);
        } else {
          value = parseFloat(item[key] || 0);
        }
        return sum + value;
      }, 0);
    },
    
    formatNumber(value) {
      if (value === null || value === undefined) return '0.00';
      return parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '';
      return moment(dateStr).format('MM/DD/YYYY');
    },
    
    getDayOfWeek(dateStr) {
      if (!dateStr) return '';
      return moment(dateStr).format('ddd').toUpperCase();
    },
    
    getAmountClass(value, isRefund = false) {
      const numValue = parseFloat(value || 0);
      if (isRefund && numValue < 0) return 'text-danger';
      if (numValue > 0) return 'text-success';
      if (numValue < 0) return 'text-danger';
      return '';
    },
    
    getShortOverClass(value) {
      const numValue = parseFloat(value || 0);
      if (numValue > 0) return 'text-success';
      if (numValue < 0) return 'text-danger';
      return '';
    },
    
    getRowBackgroundColor(item) {
      if (!item) return 'transparent';
      const shortOver = parseFloat(item.total_short_over || 0);
      if (shortOver !== 0) {
        return shortOver > 0 ? '#e8f5e8' : '#ffebee';
      }
      return 'transparent';
    },
    
    // Finance details methods
    hasFinanceDetails(item) {
      return item.finance_companies && Object.keys(item.finance_companies).length > 0;
    },
    
    getFinancePopoverContent(item) {
      if (!this.hasFinanceDetails(item)) return '';
      
      let html = '<div class="finance-popover-content">';
      const companies = item.finance_companies;
      
      Object.keys(companies).forEach(name => {
        const company = companies[name];
        html += `
          <div class="finance-popover-item">
            <span class="popover-company-name">${name}</span>
            <span class="popover-company-amount">$${this.formatNumber(company.amount)}</span>
            <span class="popover-company-count badge badge-secondary">${company.count || 1} tx</span>
          </div>
        `;
      });
      
      html += '</div>';
      return html;
    },
    
    toggleFinancePopover(item, event) {
      const id = item.id;
      this.$set(this.financePopoverStates, id, !this.financePopoverStates[id]);
      
      if (event && event.target) {
        const button = event.currentTarget;
        if (button) {
          this.$nextTick(() => {
            button.click();
          });
        }
      }
    },
    
    toggleAllFinancePopovers() {
      this.showAllFinancePopovers = !this.showAllFinancePopovers;
      
      if (this.showAllFinancePopovers) {
        this.filter.forEach(item => {
          if (this.hasFinanceDetails(item)) {
            this.$set(this.financePopoverStates, item.id, true);
          }
        });
        this.$bvToast.toast(
          "All finance details are now visible",
          { title: "Info", autoHideDelay: 1500, variant: "info" }
        );
      } else {
        this.filter.forEach(item => {
          if (this.hasFinanceDetails(item)) {
            this.$set(this.financePopoverStates, item.id, false);
          }
        });
        this.$bvToast.toast(
          "All finance details are now hidden",
          { title: "Info", autoHideDelay: 1500, variant: "info" }
        );
      }
    },
    
    isFinancePopoverVisible(item) {
      return this.financePopoverStates[item.id] || false;
    },
    
    openPDF(url) {
      if (url) {
        window.open(url, '_blank');
      }
    },
    
    openImageGallery(item) {
      if (!item) return;
      this.selectedBatch = item;
      this.selectedImages = Array.isArray(item.paid_out_images) ? item.paid_out_images : [];
      this.showImageModal = true;
    },
    
    getImageCount(item) {
      if (!item || !item.paid_out_images) return 0;
      return Array.isArray(item.paid_out_images) ? item.paid_out_images.length : 0;
    },
    
    viewFullImage(imageUrl) {
      if (!imageUrl) return;
      this.fullImageUrl = imageUrl;
      this.fullImageModal = true;
    },
    
    openImageInNewTab(imageUrl) {
      if (imageUrl) {
        window.open(imageUrl, '_blank');
      }
    },
    
    downloadImage(imageUrl, index = 0) {
      if (!imageUrl) {
        this.$bvToast.toast(
          "No image to download",
          { title: "Error", autoHideDelay: 1500, variant: "danger" }
        );
        return;
      }
      
      const link = document.createElement('a');
      link.href = imageUrl;
      const batchId = this.selectedBatch ? this.selectedBatch.id : 'batch';
      link.download = `paid_out_image_${batchId}_${index + 1}.jpg`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      this.$bvToast.toast(
        "Download started",
        { title: "Success", autoHideDelay: 1500, variant: "success" }
      );
    },
    
    resetImageGallery() {
      this.selectedBatch = null;
      this.selectedImages = [];
      this.fullImageUrl = '';
      this.fullImageModal = false;
      this.showImageModal = false;
    },
    
    exportToCSV() {
      if (this.filter.length === 0) return;
      
      const headers = [
        'BATCH #', 'DATE', 'DAY', 'LOCATION', 'SALES AGENT',
        'CASH SALES', 'CREDIT CARD', 'OTHER FINANCE', 'FINANCE DETAILS', 'REFUND', 
        'PAID OUT', 'SHORT/OVER', 'GROSS SALE', 'NET SALE', 'PDF URL', 'IMAGES URL'
      ];
      
      const rows = this.filter.map(item => [
        item.id,
        this.formatDate(item.close_date),
        this.getDayOfWeek(item.close_date),
        this.getLocationName(item.location_id),
        item.username,
        this.formatNumber(item.net_cash),
        this.formatNumber(item.credit_card_total),
        this.formatNumber(item.other_finance_total),
        this.hasFinanceDetails(item) ? Object.keys(item.finance_companies).join(', ') : '',
        this.formatNumber(item.total_refunds),
        this.formatNumber(item.paid_out_amount),
        this.formatNumber(item.total_short_over),
        this.formatNumber(item.gross_sales),
        this.formatNumber(item.net_sales),
        item.pdf_url || '',
        Array.isArray(item.paid_out_images) ? item.paid_out_images.join('; ') : ''
      ]);
      
      rows.push([
        'TOTALS', '', '', '', '',
        this.formatNumber(this.total('net_cash')),
        this.formatNumber(this.total('credit_card_total')),
        this.formatNumber(this.total('other_finance_total')),
        '',
        this.formatNumber(this.total('total_refunds')),
        this.formatNumber(this.total('paid_out_amount')),
        this.formatNumber(this.total('total_short_over')),
        this.formatNumber(this.total('gross_sales')),
        this.formatNumber(this.total('net_sales')),
        '',
        ''
      ]);
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"'))) {
            return `"${cell.replace(/"/g, '""')}"`;
          }
          return cell;
        }).join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `daily_batch_report_${moment().format('YYYYMMDD_HHmmss')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      this.$bvToast.toast(
        "CSV export completed",
        { title: "Success", autoHideDelay: 1500, variant: "success" }
      );
    }
  }
};
</script>

<style scoped>
.dashboard-container {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
  padding: 12px;
}

/* Header Styles */
.dashboard-header {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: white;
  border: none;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.header-icon-wrapper {
  width: 36px;
  height: 36px;
  background: rgba(255,255,255,0.15);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon { font-size: 18px; animation: pulse 2s infinite; }
@keyframes pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }

.dashboard-header h5 {
  color: white;
  font-weight: 600;
}

.header-subtitle {
  color: rgba(255,255,255,0.8);
}

.date-badge {
  background-color: rgba(255,255,255,0.15);
  color: white;
  border: none;
  padding: 2px 10px;
  border-radius: 14px;
  font-size: 11px;
}

.animated-badge {
  animation: fadeIn 0.5s ease-out;
}

/* Filters Card */
.filters-card {
  border: none;
  border-radius: 14px;
  box-shadow: 0 1px 5px rgba(0,0,0,0.05);
  background: white;
}

.custom-datepicker {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 4px 8px;
  font-size: 12px;
  width: 100%;
}

.datepicker-focused {
  border-color: #6366f1;
  box-shadow: 0 0 0 2px rgba(99,102,241,0.1);
}

.quick-date-btn {
  border-radius: 14px;
  font-size: 11px;
  padding: 3px 8px;
}

.filter-select {
  border-radius: 7px;
  background: #f8fafc;
  border-color: #e2e8f0;
  font-size: 12px;
}

.search-btn {
  border-radius: 8px;
  padding: 4px 20px;
  font-weight: 600;
  font-size: 12px;
}

/* Compact KPI Cards */
.compact-kpi-card {
  background: white;
  border-radius: 10px;
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  transition: all 0.2s;
}

.compact-kpi-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(0,0,0,0.1);
}

.compact-kpi-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
}

.bg-primary { background: linear-gradient(135deg, #6366f1, #8b5cf6); }
.bg-success { background: linear-gradient(135deg, #10b981, #34d399); }
.bg-warning { background: linear-gradient(135deg, #f59e0b, #fbbf24); }
.bg-info { background: linear-gradient(135deg, #06b6d4, #22d3ee); }
.bg-danger { background: linear-gradient(135deg, #ef4444, #f87171); }

.compact-kpi-info {
  display: flex;
  flex-direction: column;
}

.compact-kpi-label {
  font-size: 10px;
  color: #64748b;
  display: block;
}

.compact-kpi-value {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  display: block;
  line-height: 1.2;
}

.compact-kpi-sub {
  font-size: 9px;
  color: #94a3b8;
}

/* Data Table */
.data-table-card {
  border: none;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  overflow: hidden;
}

.table-wrapper-sm {
  overflow-x: auto;
}

.compact-table {
  width: 100%;
  font-size: 11px;
  border-collapse: collapse;
}

.compact-table th,
.compact-table td {
  padding: 6px 8px;
  border: 1px solid #dee2e6;
}

.compact-table th {
  background: #c3cdd3;
  font-weight: 600;
  position: sticky;
  top: 0;
  white-space: nowrap;
  cursor: pointer;
}

.compact-table td {
  white-space: nowrap;
}

.compact-table tbody tr:hover {
  background: #e0e7ff !important;
}

.highlight-short-row {
  background: #fffbeb;
}

.totals-row {
  background: #fef3c7;
  font-weight: 600;
}

.totals-row td {
  background: #fef3c7;
}

.text-right {
  text-align: right !important;
}

.text-center {
  text-align: center !important;
}

.text-success {
  color: #28a745 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-warning {
  color: #f59e0b !important;
}

.action-btn {
  padding: 2px 6px;
  font-size: 11px;
}

/* Finance Details Button */
.finance-detail-btn {
  padding: 2px 8px;
  font-size: 11px;
  border-radius: 14px;
  white-space: nowrap;
}

.finance-detail-btn:hover {
  background: #e8f4fd;
}

.finance-toggle-header {
  color: #6c757d;
  font-size: 12px;
  padding: 0 4px;
}

.finance-toggle-header:hover {
  color: #0d6efd;
}

/* Finance Popover Styles */
.finance-popover-content {
  padding: 4px 0;
  min-width: 200px;
}

.finance-popover-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  border-bottom: 1px solid #e9ecef;
  gap: 12px;
}

.finance-popover-item:last-child {
  border-bottom: none;
}

.popover-company-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 12px;
}

.popover-company-amount {
  font-weight: 500;
  color: #0d6efd;
  font-size: 12px;
}

.popover-company-count {
  font-size: 10px;
  padding: 1px 6px;
}

/* Summary Card */
.summary-card {
  border: none;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  background: white;
}

.summary-label {
  font-size: 10px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-value {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 2px 0 0 0;
}

.text-primary { color: #6366f1 !important; }
.text-success { color: #10b981 !important; }
.text-danger { color: #ef4444 !important; }

.pagination-wrapper {
  border-top: 1px solid #e2e8f0;
}

.border-top {
  border-top: 1px solid #e2e8f0;
}

/* Image Gallery */
.image-gallery {
  padding: 10px 0;
}

.image-card {
  background: #f8fafc;
  border-radius: 8px;
  padding: 10px;
  transition: all 0.2s;
}

.image-card:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.image-actions {
  margin-top: 8px;
}

/* Popover Custom Styles */
::v-deep .popover {
  max-width: 350px !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
  border: 1px solid #e2e8f0 !important;
}

::v-deep .popover-header {
  background: #f8fafc !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  border-bottom: 1px solid #e2e8f0 !important;
  padding: 8px 12px !important;
}

::v-deep .popover-body {
  padding: 8px 0 !important;
  max-height: 300px;
  overflow-y: auto;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.compact-kpi-card, .data-table-card, .filters-card, .summary-card {
  animation: fadeIn 0.5s ease-out;
}

/* Responsive */
@media (max-width: 768px) {
  .dashboard-container { padding: 8px; }
  .compact-kpi-value { font-size: 13px; }
  .compact-table th, .compact-table td { padding: 4px 6px; font-size: 9px; }
  .compact-kpi-icon { width: 32px; height: 32px; font-size: 14px; }
  .dashboard-header h5 { font-size: 14px; }
  .header-subtitle { font-size: 10px; }
  .summary-value { font-size: 1rem; }
  .finance-popover-item { flex-wrap: wrap; gap: 4px; }
  .popover-company-name { font-size: 11px; }
  .popover-company-amount { font-size: 11px; }
}
</style>


// WEBPACK FOOTER //
// src/components/Reports/DailyBatchReport.vue